import { useState } from 'react';
import { listInboxes, setSmsDefault } from '../../api/endpoints';
import type { SmsDefaultPrompt as Prompt } from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Spinner } from '../../components/Spinner';
import { replaceQuestion } from '../../lib/smsDefault';
import { useLoad } from '../../lib/useLoad';

/**
 * What a newly connected number means for automated SMS — the texts the
 * dispatch system sends — asked once the connection works. WHICH question is
 * the server's answer (services/smsDefault.js, via POST /inboxes or a
 * RingCentral sign-in's return); this only words it, naming the current default
 * from the inbox list.
 */
export function SmsDefaultPrompt({ inboxId, identity, prompt }: { inboxId: number; identity: string; prompt: Prompt }) {
  const inboxes = useLoad(listInboxes);
  const [answer, setAnswer] = useState<'yes' | 'no' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (prompt === 'auto') {
    return <Note tone="info">This number is now your default for automated SMS.</Note>;
  }
  if (answer === 'yes') {
    return (
      <Note tone="ok">
        <strong>{identity}</strong> is now your default sender for automated SMS.
      </Note>
    );
  }
  if (answer === 'no') {
    return (
      <Note tone="info">
        {prompt === 'ask_replace'
          ? 'Your default sender for automated SMS is unchanged — this number is connected for regular SMS.'
          : 'Automated SMS stays off — this number is connected for regular SMS.'}
      </Note>
    );
  }
  if (prompt === 'ask_replace' && !inboxes.data && !inboxes.error) return <Spinner />;

  // The list can move between the server's answer and this render. A default
  // that has gone since is asked about as turning automated SMS on — which is
  // what saying Yes would now do.
  const current = inboxes.data?.find((inbox) => inbox.defaultSmsSender && inbox.id !== inboxId) ?? null;
  const question =
    prompt === 'ask_replace' && current
      ? replaceQuestion(current, identity)
      : 'Do you want to use this number to send automated SMS?';
  const replacing = prompt === 'ask_replace' && current !== null;

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      await setSmsDefault(inboxId);
      setAnswer('yes');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the default sender.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sms-default-prompt" role="group" aria-label="Automated SMS">
      {error && <ErrorNote error={error} />}
      <p className="sms-default-question">{question}</p>
      <div className="connect-panel-actions">
        <button className="btn btn-primary" onClick={() => void accept()} disabled={busy}>
          {busy && <Spinner />}
          {replacing ? 'Replace' : 'Yes'}
        </button>
        <button className="btn btn-secondary" onClick={() => setAnswer('no')} disabled={busy}>
          {replacing ? 'Keep current' : 'No'}
        </button>
      </div>
    </div>
  );
}
